/**
 * Clean this up along with platform_editor_ai_aifc_confluence
 */
import React from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import Tooltip from '@atlaskit/tooltip';
import { IconButton } from '@atlaskit/button/new';
import RedoIcon from '@atlaskit/icon/core/redo';
import UndoIcon from '@atlaskit/icon/core/undo';

import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { useAIRovoButtonEnablement } from '@confluence/ai-floating-context-menu/entry-points/useAIRovoButtonEnablement';
import { useContentType } from '@confluence/page-context';
import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import {
	ShortcutVisualizer,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import { EDITOR2_SHORTCUTS } from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { fg } from '@confluence/feature-gating';

import { AnalyticContexts } from './analyticsUtils';

const i18n = defineMessages({
	undoLabel: {
		id: 'object-sidebar-components.undo.editor.action.label',
		defaultMessage: 'Undo',
		description: 'Label for button to undo an editor action',
	},
	redoLabel: {
		id: 'object-sidebar-components.redo.editor.action.label',
		defaultMessage: 'Redo',
		description: 'Label for button to redo an editor action',
	},
});

type UndoRedoButtonType = 'undo' | 'redo';

interface UndoRedoButtonProps {
	type: UndoRedoButtonType;
	canPerformAction: boolean;
	onAction: () => boolean;
	isDisabled?: boolean;
}

const capitalize = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s);

const UndoRedoButton = ({
	type,
	canPerformAction,
	onAction,
	isDisabled = false,
}: UndoRedoButtonProps) => {
	const pageMode = useGetPageMode();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const [contentTypeFromState] = useContentType();
	const { formatMessage } = useIntl();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	// keyboard events do not bubble up to confluence when focus is in the db iframe.
	// Since the keyboard shortcut does not work, we must hide the shortcut text.
	const isShortcutTextShown = contentTypeFromState !== ContentType.DATABASE;

	// Configure button based on type
	const config = {
		undo: {
			icon: UndoIcon,
			shortcut: EDITOR2_SHORTCUTS.UNDO,
			label: i18n.undoLabel,
			testId: 'sidebar-undo-button',
		},
		redo: {
			icon: RedoIcon,
			shortcut: EDITOR2_SHORTCUTS.REDO,
			label: i18n.redoLabel,
			testId: 'sidebar-redo-button',
		},
	}[type];

	const analyticsContext = {
		source: AnalyticContexts.Source,
		name: `${AnalyticContexts.Source}${capitalize(type)}Button`,

		attributes: {
			source: AnalyticContexts.Source,
			name: `${AnalyticContexts.Source}${capitalize(type)}Button`,
		},
	};

	const shortcutStrings = useShortcutStrings({
		shortcut: config.shortcut,
		isEditorShortcut: pageMode === 'edit' || isLiveEditMode,
	});

	return (
		<Tooltip
			position="left"
			content={
				isShortcutTextShown && !fg('platform_tooltip_shortcuts_migration_1') ? (
					<ShortcutVisualizer
						shortcut={config.shortcut}
						contentBefore={formatMessage(config.label)}
						isEditorShortcut={pageMode === 'edit' || isLiveEditMode}
						isTooltip
					/>
				) : (
					<FormattedMessage {...config.label} />
				)
			}
			shortcut={
				isShortcutTextShown && fg('platform_tooltip_shortcuts_migration_1')
					? shortcutStrings
					: undefined
			}
			delay={0}
		>
			<IconButton
				icon={config.icon}
				appearance="subtle"
				label=""
				testId={config.testId}
				shape={isRovoButtonEnabled ? 'default' : 'circle'}
				onClick={() => {
					onAction();
				}}
				tooltip={{
					position: 'left',
				}}
				isDisabled={!canPerformAction || isDisabled}
				analyticsContext={analyticsContext}
			/>
		</Tooltip>
	);
};

export const UndoButton = ({
	canUndo,
	undo,
	isDisabled = false,
}: {
	canUndo: boolean;
	undo: () => boolean;
	isDisabled?: boolean;
}) => {
	return (
		<UndoRedoButton
			type="undo"
			canPerformAction={canUndo}
			onAction={undo}
			isDisabled={isDisabled}
		/>
	);
};

export const RedoButton = ({
	canRedo,
	redo,
	isDisabled = false,
}: {
	canRedo: boolean;
	redo: () => boolean;
	isDisabled?: boolean;
}) => {
	return (
		<UndoRedoButton
			type="redo"
			canPerformAction={canRedo}
			onAction={redo}
			isDisabled={isDisabled}
		/>
	);
};

export { UndoRedoButton };
